<template>
  <div>
    <l-map ref="thePickerMap"
    :zoom.sync="currentZoom"
    :options="mapOptions"
    :center="center"
    :min-zoom="minZoom"
    :max-zoom="maxZoom"
    :bounds.sync="bounds"
    :style="'height: ' + height + '; width: 100%;'"
    @ready="emitReady"
    >
      <l-tile-layer
        v-for="tileProvider in tileProvidersLocal"
        :key="tileProvider.name"
        :name="tileProvider.name"
        :visible="tileProvider.visible"
        :url="tileProvider.url"
        :attribution="tileProvider.attribution"
        layer-type="base"
        :options="tileProvider.options"
        v-if="tileProvidersReady"
      />
      <l-control-layers
        :position="layersPosition"
        :collapsed="false"
        :sort-layers="true"
      />
      <l-control-zoom :position="zoomPosition" />
      <l-control-attribution
        :position="attributionPosition"
        prefix="Moova"
      />
      <l-control-scale position="bottomright" :imperial="imperial" :metric="metric" />

      <l-control position="bottomleft">
        <div class="map-filters" v-if="computedGeoTypesSelect.length > 0" :class="parentData.length > 0 ? 'd-none' : ''">
          <b-card class="mb-0 p-1">
            <strong>
              <translate>Show Nearby Elements</translate>
              <strong class="text-danger" v-if="nearbyLayersSwitch && currentZoom <= 12" :title="$gettext('Zoom-in to Search Again')" v-b-tooltip.hover>(!)</strong>
            </strong>
            <indeterminate-checkboxes all-selected :parent-title="$gettext('GeoFences') + ` (${geoFences.length})`" :parent-title-all-selected="$gettext('GeoFences') + ` (${geoFences.length})`" v-model="polygonTypes" :child-options="computedGeoTypesSelect.filter(x => x.type === 'polygon')" />
            <indeterminate-checkboxes all-selected :parent-title="$gettext('GeoLines') + ` (${geoLines.length})`" :parent-title-all-selected="$gettext('GeoLines') + ` (${geoLines.length})`" v-model="lineTypes" :child-options="computedGeoTypesSelect.filter(x => x.type === 'linestring')" />
            <indeterminate-checkboxes all-selected :parent-title="$gettext('GeoPoints') + ` (${geoPoints.length})`" :parent-title-all-selected="$gettext('GeoPoints') + ` (${geoPoints.length})`" v-model="pointTypes" :child-options="computedGeoTypesSelect.filter(x => x.type === 'point')" />
          </b-card>
        </div>
      </l-control>

      <l-feature-group ref="nearbyLayers" id="nearbyLayers" :visible="nearbyLayersSwitch">
        <l-polygon
          v-for="(geofence, index) in geoFences"
          :key="'notLinkedGeofence' + index"
          v-if="geoFences.length > 0 && geofence.coordinates"
          :lat-lngs="geofence.coordinates"
          :editable="geofence.editable"
          :visible="polygonTypes.length > 0"
          :color="stringToColour('11' + geoTypes.find(x => x.id === geofence.geo_type_id).name)"
          :fillColor="stringToColour('11' + geoTypes.find(x => x.id === geofence.geo_type_id).name)"
          :weight="2"
        >
          <l-popup :options="{closeButton: false}">
            <b-button v-if="selectedElements.filter(x => x.type === 'geo-fences').map(x => x.id).indexOf(geofence.id) === -1" @click="selectElement($event, geofence, 'geo-fences'); map.closePopup();" variant="success" :title="$gettext('Select GeoFence')" v-b-tooltip.hover><font-awesome-icon icon="check" /></b-button>
            <b-button v-else @click="unSelectElement($event, geofence, 'geo-fences'); map.closePopup();" variant="danger" :title="$gettext('Un-select GeoFence')" v-b-tooltip.hover><font-awesome-icon icon="trash-alt" /></b-button>
          </l-popup>
          <l-tooltip>
            <geofence-title :geofence="geofence" />
          </l-tooltip>
        </l-polygon>

        <l-polyline
          v-for="(line, index) in geoLines"
          :key="'notLinkedLine' + index"
          :lat-lngs="line.coordinates"
          :editable="line.editable"
          :visible="lineTypes.length > 0"
          :color="geoTypes.find(x => x.id === line.geo_type_id).name.indexOf('entry') !== -1 ? '#00cc00' : (geoTypes.find(x => x.id === line.geo_type_id).name.indexOf('exit') !== -1 ? '#cc0000' : '#000')"
          :weight="2"
          @editable:editing="updateLine($event, index)"
        >
          <l-popup :options="{closeButton: false}">
            <b-button v-if="selectedElements.filter(x => x.type === 'geo-lines').map(x => x.id).indexOf(line.id) === -1" @click="selectElement($event, line, 'geo-lines'); map.closePopup();" variant="success" :title="$gettext('Select GeoLine')" v-b-tooltip.hover><font-awesome-icon icon="check" /></b-button>
            <b-button v-else @click="unSelectElement($event, line, 'geo-lines'); map.closePopup();" variant="danger" :title="$gettext('Un-select GeoLine')" v-b-tooltip.hover><font-awesome-icon icon="trash-alt" /></b-button>
          </l-popup>
          <l-tooltip>
            {{line.name}}
          </l-tooltip>
        </l-polyline>

        <l-marker :lat-lng="point" v-for="(point, index) in geoPoints" :key="'notLinkedPoint' + index" :draggable="point.editable" @update:latLng="updatePoint($event, index)" :visible="pointTypes.length > 0">
          <l-icon
            :icon-anchor="[9, 24]"
            style="position: relative;"
            :tooltipAnchor="[9, -12]"
            :icon-size="[18, 24]"
          >
            <font-awesome-icon icon="map-marker" size="2x" style="filter: drop-shadow(0 0 1px rgba(255,255,255,1)) drop-shadow(0 2px 2px rgba(0,0,0,.3));" :color="geoTypes.find(x => x.id === point.geo_type_id).name.indexOf('entry') !== -1 ? '#00cc00' : (geoTypes.find(x => x.id === point.geo_type_id).name.indexOf('exit') !== -1 ? '#cc0000' : '#000')" />
          </l-icon>
          <l-popup :options="{closeButton: false}">
            <b-button v-if="selectedElements.filter(x => x.type === 'geo-points').map(x => x.id).indexOf(point.id) === -1" @click="selectElement($event, point, 'geo-points'); map.closePopup();" variant="success" :title="$gettext('Select GeoPoint')" v-b-tooltip.hover><font-awesome-icon icon="check" /></b-button>
            <b-button v-else @click="unSelectElement($event, point, 'geo-points'); map.closePopup();" variant="danger" :title="$gettext('Un-select GeoPoint')" v-b-tooltip.hover><font-awesome-icon icon="trash-alt" /></b-button>
          </l-popup>
          <l-tooltip>
            {{point.name}}
          </l-tooltip>
        </l-marker>
      </l-feature-group>

      <map-marker
        v-for="marker in markers"
        :marker="marker"
        :key="marker.id"
        :lat-lng="[marker.lat, marker.lng]"
        :visible="true"
        :draggable="false"
      >
        <l-icon
          :iconUrl="require('leaflet/dist/images/marker-icon.png')"
        >
        </l-icon>
      </map-marker>
    </l-map>
    <form @submit.prevent="emitSelectedElements" class="my-3">
      <div class="form-group col-sm-12" v-if="selectedElements.length > 0">
        <h3>Selected Elements</h3>
        <draggable
          class="list-group"
          tag="ul"
          v-model="selectedElements"
          v-bind="dragOptions"
          @start="drag = true"
          @end="drag = false"
          handle=".handle"
        >
          <transition-group type="transition" :name="!drag ? 'flip-list' : null">
            <li
              class="list-group-item d-flex justify-content-between align-items-center"
              v-for="(element, index) in selectedElements"
              :key="'selectedElements' + index"
            >
              <div>
                <span v-if="element.dupe">
                  <font-awesome-icon icon="exclamation-triangle" class="text-warning" />
                </span>
                <strong>{{element.external_id}}</strong> {{ element.name }} <small>[{{ element.geo_type ? element.geo_type.name : geoTypes.find(x => x.id === element.geo_type_id).name }}]</small>
              </div>
              <b-button size="sm" variant="light" class="handle mr-2"><font-awesome-icon icon="arrows-alt-v" fixed-width /></b-button>
            </li>
          </transition-group>
        </draggable>
      </div>
      <div class="form-group col-12 form-actions">
        <b-button variant="secondary" type="submit" :disabled="selectedElements.length === 0" v-translate>
          Confirm Extra Data
        </b-button>
        <b-button variant="outline-secondary" size="md" @click.prevent="emitCancel" v-translate>
          Cancel
        </b-button>
      </div>
    </form>
  </div>
</template>
<script>
import {
  GeofenceService,
  GeoLineService,
  GeoPointService
} from '@/services/api'
import IndeterminateCheckboxes from '@/components/IndeterminateCheckboxes'
import Draggable from 'vuedraggable'
import AddressesMixin from '@/mixins/address.mixin'
import {
  LMap,
  LTileLayer,
  LFeatureGroup,
  LPolygon,
  LPolyline,
  LMarker,
  LControlZoom,
  LControlAttribution,
  LControlScale,
  LControlLayers,
  LControl,
  LIcon,
  LTooltip,
  LPopup
} from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'
import MapMarker from '@/components/Maps/MapMarker'

export default {
  name: 'PlacePicker',
  components: {
    IndeterminateCheckboxes,
    Draggable,
    LMap,
    LTileLayer,
    LFeatureGroup,
    LPolygon,
    LPolyline,
    LMarker,
    LControlZoom,
    LControlAttribution,
    LControlScale,
    LControlLayers,
    LControl,
    LIcon,
    LTooltip,
    LPopup,
    MapMarker
  },
  data() {
    return {
      reference: '',
      icon: null,
      places: [],
      currentPlace: null,
      map: null,
      mapReady: false,
      center: [-34.6158036, -58.5035093],
      opacity: 1,
      mapOptions: {
        zoomControl: false,
        attributionControl: false,
        zoomSnap: true
      },
      minZoom: 1,
      maxZoom: 18,
      zoomPosition: 'topleft',
      layersPosition: 'topright',
      attributionPosition: 'bottomright',
      imperial: false,
      metric: false,
      markers: [],
      bounds: null,
      fitted: false,
      tileProvidersReady: false,
      tileProvidersLocal: [],
      geoFences: [],
      geoLines: [],
      geoPoints: [],
      currentZoom: 13,
      polygonTypes: [],
      lineTypes: [],
      pointTypes: [],
      searching: {
        geoFences: false,
        geoLines: false,
        geoPoints: false
      },
      acGeofence: false,
      acGeoline: false,
      acGeopoint: false,
      nearbyLayersSwitch: true,
      selectedElements: [],
      drag: false,
      dragOptions: {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost'
      }
    }
  },
  props: {
    geolocate: {
      type: Boolean,
      default: false
    },
    fitOnce: {
      type: Boolean,
      default: false
    },
    fitAlways: {
      type: Boolean,
      default: false
    },
    zoom: {
      type: Number,
      default: 11
    },
    centerMap: {
      type: Boolean,
      default: false
    },
    hasReference: {
      type: Boolean,
      default: false
    },
    height: {
      type: String,
      default: '300px'
    },
    visualReference: {
      type: Object,
      default: () => {}
    },
    parentData: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    computedGeoTypesSelect() {
      return this.geoTypes.map(x => {
        return {
          text: this.toTitleCase(x.name),
          value: x.id,
          type: x.type
        }
      })
    }
  },
  watch: {
    tileProviders: {
      handler(newVal, oldVal) {
        this.tileProvidersLocal = JSON.parse(JSON.stringify(newVal))
        this.setDefaultTileProvider()
      },
      deep: true
    },
    bounds: {
      handler(newVal, oldVal) {
        this.map.closePopup()
        if (this.parentData.length === 0) { // TODO: try to support both parentData *AND* nearbyElements
          this.getNearbyElements()
        }
      },
      deep: true
    },
    polygonTypes(val) {
      if (this.parentData.length === 0) {
        this.getNearbyElements()
      }
    },
    lineTypes(val) {
      if (this.parentData.length === 0) {
        this.getNearbyElements()
      }
    },
    pointTypes(val) {
      if (this.parentData.length === 0) {
        this.getNearbyElements()
      }
    }
  },
  mixins: [
    AddressesMixin
  ],
  async mounted() {
    this.imperial = this.frontConfig?.i18n?.unitSystem === 'imperial'
    this.metric = !this.imperial
    await this.checkMapProviders()
    this.tileProvidersLocal = JSON.parse(JSON.stringify(this.tileProviders))
    this.setDefaultTileProvider()
    if (this.visualReference) {
      this.markers = [
        this.transformMarker(
          {
            id: '0',
            lat: this.visualReference.lat,
            lng: this.visualReference.lng,
            icon: this.visualReference.icon,
            x: 10,
            y: 10
          }
        )
      ]
    }
    this.currentZoom = this.zoom
    if (this.parentData.length > 0) {
      console.log(this.parentData)
      this.geoFences = this.parentData.filter(x => this.geoTypes.find(y => y.id === x.geo_type_id).type === 'polygon').map(x => { return {...this.setFenceCoordinates(x), editable: false} })
      this.geoLines = this.parentData.filter(x => this.geoTypes.find(y => y.id === x.geo_type_id).type === 'linestring').map(x => { return {...this.setLineCoordinates(x), editable: false} })
      this.geoPoints = this.parentData.filter(x => this.geoTypes.find(y => y.id === x.geo_type_id).type === 'point').map(x => { return {...this.setPointCoordinates(x), editable: false} })
      this.selectedElements = JSON.parse(JSON.stringify(this.parentData))
    }
  },
  beforeDestroy() {
    this.tileProvidersLocal = []
    this.map = null
  },
  methods: {
    setFenceCoordinates(geofence) {
      // console.log('setFenceCoordinates > ', geofence)

      // Backup
      geofence.originalFence = !geofence.originalFence ? Object.assign({}, geofence.fence) : geofence.originalFence

      // For map
      let cleanupDupes = geofence.fence && geofence.fence.coordinates ? [Array.from(new Set(geofence.fence.coordinates[0].map(a => a.join('|'))), s => s.split('|').map(Number))] : null
      geofence.coordinates = cleanupDupes ? cleanupDupes.map(x =>
        x.map(([lng, lat]) => ({lat, lng}))
      ) : geofence.coordinates

      // To save
      geofence.fence = cleanupDupes ? cleanupDupes.map(x => x.map(y => [y[1], y[0]]))[0] : geofence.fence
      geofence.type = 'geo-fences'
      return geofence
    },
    setLineCoordinates(line) {
      // console.log('setLineCoordinates > ', line)

      // Backup
      line.originalPath = !line.originalPath ? Object.assign({}, line.path) : line.originalPath

      // For map
      let cleanupDupes = line.path && line.path.coordinates ? Array.from(new Set(line.path.coordinates.map(a => a.join('|'))), s => s.split('|').map(Number)) : null
      line.coordinates = cleanupDupes ? cleanupDupes.map(([lng, lat]) => ({lat, lng})) : line.coordinates

      // To save
      line.path = cleanupDupes ? cleanupDupes.map(x => [x[1], x[0]]) : line.path
      line.type = 'geo-lines'
      return line
    },
    setPointCoordinates(point) {
      point.type = 'geo-points'
      return point
    },
    async getNearbyElements() {
      if (this.nearbyLayersSwitch && this.map && this.map.getZoom() > 12 && !this.editing) {
        if (this.polygonTypes.length > 0) {
          if (!this.searching.geoFences) {
            this.searching.geoFences = true
            let params = {
              includes: [
                'geo_type'
              ],
              filter_groups: [
                {
                  filters: [
                    ['geo_type_id', 'in', this.polygonTypes],
                    ['boundary', 'eq', this.bounds._southWest.lat + ',' + this.bounds._southWest.lng + '|' + this.bounds._northEast.lat + ',' + this.bounds._northEast.lng]
                  ]
                }
              ]
            }
            GeofenceService.getAll({params})
              .then(response => {
                this.geoFences = []
                this.$nextTick(() => {
                  this.geoFences = response.body.data.map(x => { return {...this.setFenceCoordinates(x), editable: false} })
                  this.searching.geoFences = false
                })
              })
          }
        } else {
          this.geoFences = []
          this.searching.geoFences = false
        }
        if (this.lineTypes.length > 0) {
          if (!this.searching.geoLines) {
            this.searching.geoLines = true
            let params = {
              includes: [
                'geo_type'
              ],
              filter_groups: [
                {
                  filters: [
                    ['geo_type_id', 'in', this.lineTypes],
                    ['boundary', 'eq', this.bounds._southWest.lat + ',' + this.bounds._southWest.lng + '|' + this.bounds._northEast.lat + ',' + this.bounds._northEast.lng]
                  ]
                }
              ]
            }
            GeoLineService.getAll({params})
              .then(response => {
                this.geoLines = []
                this.$nextTick(() => {
                  this.geoLines = response.body.data.map(x => { return {...this.setLineCoordinates(x), editable: false} })
                  this.searching.geoLines = false
                })
              })
          }
        } else {
          this.geoLines = []
          this.searching.geoLines = false
        }
        if (this.pointTypes.length > 0) {
          if (!this.searching.geoPoints) {
            this.searching.geoPoints = true
            let params = {
              includes: [
                'geo_type'
              ],
              filter_groups: [
                {
                  filters: [
                    ['geo_type_id', 'in', this.pointTypes],
                    ['boundary', 'eq', this.bounds._southWest.lat + ',' + this.bounds._southWest.lng + '|' + this.bounds._northEast.lat + ',' + this.bounds._northEast.lng]
                  ]
                }
              ]
            }
            GeoPointService.getAll({params})
              .then(response => {
                this.geoPoints = []
                this.$nextTick(() => {
                  this.geoPoints = response.body.data.map(x => { return {...this.setPointCoordinates(x), editable: false} })
                  this.searching.geoPoints = false
                })
              })
          }
        } else {
          this.geoPoints = []
          this.searching.geoPoints = false
        }
      }
    },
    setDefaultTileProvider() {
      if (this.ecosystemMaps && this.ecosystemMaps !== null && this.ecosystemMaps.defaultTileProvider && this.ecosystemMaps.defaultTileProvider[this.$options.name]) {
        this.tileProvidersLocal = this.tileProvidersLocal.map(x => {
          if (x.name === this.ecosystemMaps.defaultTileProvider[this.$options.name]) {
            x.visible = true
          } else {
            x.visible = false
          }
          return x
        })
      }
      this.tileProvidersReady = true
    },
    async emitSelectedElements() {
      this.$emit('selectedElements', this.selectedElements)
    },
    emitCancel() {
      this.$emit('cancel')
    },
    emitReady() {
      this.map = this.$refs.thePickerMap.mapObject
      this.mapReady = true
      this.$emit('ready', true)
      this.setDefaultTileProvider()
      setTimeout(function () {
        this.map.invalidateSize()
        if (this.parentData.length > 0) {
          let layer = this.$refs.nearbyLayers.mapObject
          this.map.fitBounds(layer.getBounds())
        }
      }.bind(this), 500)
    },
    transformMarker(item) {
      return {
        id: item.id,
        position: {
          lat: item.lat,
          lng: item.lng
        },
        icon: typeof item.icon === 'string' ? {
          iconUrl: item.icon,
          iconAnchor: item.anchor || [item.x, item.y],
          iconSize: item.size || [item.x * 2, item.y * 2],
          tooltipAnchor: item.tooltipAnchor || [item.x, 0],
          popupAnchor: item.popupAnchor || [0, -item.y]
        } : {
          iconType: item.icon.type,
          iconAnchor: item.icon.anchor,
          iconSize: item.icon.size,
          tooltipAnchor: item.icon.tooltipAnchor || [item.x, 0],
          popupAnchor: item.icon.popupAnchor || [0, -item.y],
          iconColor: item.icon.color,
          iconText: item.icon.text,
          iconRotation: item.icon.direction ? (item.icon.direction <= 180 ? item.icon.direction : item.icon.direction - 360) : 0
        },
        infoText: item.infoText,
        tooltip: item.tooltip || null,
        popup: item.popup || null
      }
    },
    selectElement($event, element, type) {
      this.selectedElements.push({...element, type})
      this.$nextTick(() => {
        this.selectedElements = this.checkDistinctExtraPoints(this.selectedElements)
      })
    },
    unSelectElement($event, element, type) {
      this.selectedElements = this.selectedElements.filter(x => x.type !== type || x.type === type && x.id !== element.id)
      this.$nextTick(() => {
        this.selectedElements = this.checkDistinctExtraPoints(this.selectedElements)
      })
    },
    checkDistinctExtraPoints(elements) {
      elements = elements.map(x => {
        if (elements.filter(y => y.geo_type_id === x.geo_type_id).length > 1) {
          x.dupe = true
        } else {
          x.dupe = false
        }
        return x
      })
      return elements
    }
  }
}

</script>
